/**
 * Renders the per-oracle outcomes from `verifyOnboardingReady`.
 *
 * Two sections — Login Readiness (oracles 1-4) and Dashboard Readiness
 * (oracles 5-8). Each row shows severity icon, dotted name, detail (when
 * failed), and `measured` chips (when passed/diagnostic).
 *
 * @see services/admin-functions/src/verification/onboardingReadinessTypes.ts
 */

import React from 'react';
import { CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import type { OracleResult } from '../types/onboardingReadiness';

interface SeverityIconProps {
  result: OracleResult;
}

const SeverityIcon: React.FC<SeverityIconProps> = ({ result }) => {
  if (result.passed) {
    return <CheckCircle className="w-5 h-5 text-green-400" aria-label="Passed" />;
  }
  if (result.severity === 'CRITICAL') {
    return <XCircle className="w-5 h-5 text-red-400" aria-label="Critical failure" />;
  }
  return <AlertTriangle className="w-5 h-5 text-yellow-400" aria-label="Warning" />;
};

interface MeasuredChipsProps {
  measured?: Record<string, number | string>;
}

const MeasuredChips: React.FC<MeasuredChipsProps> = ({ measured }) => {
  if (!measured) return null;
  const entries = Object.entries(measured);
  if (entries.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1.5 mt-1.5">
      {entries.map(([k, v]) => (
        <span
          key={k}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-slate-700/40 border border-slate-600/40 text-slate-300 font-mono"
        >
          <span className="text-slate-400">{k}:</span>
          <span className="text-slate-100">{String(v)}</span>
        </span>
      ))}
    </div>
  );
};

interface OracleSectionProps {
  title: string;
  oracles: OracleResult[];
}

const OracleSection: React.FC<OracleSectionProps> = ({ title, oracles }) => {
  const passedCount = oracles.filter((o) => o.passed).length;
  const totalCount = oracles.length;
  const allPassed = passedCount === totalCount && totalCount > 0;

  return (
    <div className="bg-slate-800/30 backdrop-blur-sm rounded-xl border border-slate-700/50 overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-700/50 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-white">{title}</h3>
        <span
          className={`px-3 py-1 text-xs font-semibold rounded-full border uppercase tracking-wide ${
            allPassed
              ? 'bg-green-500/20 text-green-400 border-green-500/30'
              : 'bg-red-500/20 text-red-400 border-red-500/30'
          }`}
        >
          {passedCount} / {totalCount} passed
        </span>
      </div>
      <div className="divide-y divide-slate-700/30">
        {oracles.length === 0 ? (
          <div className="px-6 py-8 text-center text-slate-400 text-sm">
            No oracles returned.
          </div>
        ) : (
          oracles.map((result) => (
            <div
              key={result.name}
              className="px-6 py-4 flex items-start gap-4 hover:bg-slate-800/20 transition-colors"
            >
              <div className="pt-0.5 flex-shrink-0">
                <SeverityIcon result={result} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-mono text-slate-100 font-semibold">
                    {result.name}
                  </span>
                  {!result.passed && (
                    <span
                      className={`px-2 py-0.5 text-xs font-semibold rounded uppercase tracking-wide ${
                        result.severity === 'CRITICAL'
                          ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                          : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                      }`}
                    >
                      {result.severity}
                    </span>
                  )}
                </div>
                {result.detail && (
                  <p className="text-sm text-slate-300 mt-1.5 break-words">
                    {result.detail}
                  </p>
                )}
                <MeasuredChips measured={result.measured} />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export interface OracleResultsTableProps {
  loginOracles: OracleResult[];
  dashboardOracles: OracleResult[];
}

export const OracleResultsTable: React.FC<OracleResultsTableProps> = ({
  loginOracles,
  dashboardOracles,
}) => {
  return (
    <div className="space-y-6">
      <OracleSection title="Login Readiness" oracles={loginOracles} />
      <OracleSection title="Dashboard Readiness" oracles={dashboardOracles} />
    </div>
  );
};

export default OracleResultsTable;
